import axios from 'axios';
import { IAuthService, User, AuthResponse } from '../interfaces/IAuthService';
import { API_URL } from '../global/global';

class AuthService implements IAuthService {
  private baseURL = API_URL;
  private tokenKey = 'skybot_token';
  private userKey = 'skybot_user';
  private skyoneTokenKey = 'skyone_token';
  private loginTimeKey = 'skybot_login_time';

  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${this.baseURL.replace('/api', '')}/auth/login/`, {
        email,
        password
      });

      const data = response.data[0];
      
      if (data.success && data.access_token) {
        localStorage.setItem(this.tokenKey, data.access_token);
        localStorage.setItem(this.userKey, JSON.stringify(data.user));
        localStorage.setItem(this.loginTimeKey, Date.now().toString());
        
        // Guardar SkyOne token si está disponible
        if (data.skyone_token) {
          localStorage.setItem(this.skyoneTokenKey, data.skyone_token);
        }
      }

      return data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error de conexión'
      };
    }
  }

  async register(username: string, password: string, email: string): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${this.baseURL}/auth/register`, {
        username,
        password,
        email
      });

      const data = response.data;
      
      if (data.success && data.access_token) {
        localStorage.setItem(this.tokenKey, data.access_token);
        localStorage.setItem(this.userKey, JSON.stringify(data.user));
        localStorage.setItem(this.loginTimeKey, Date.now().toString());
      }

      return data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error de conexión'
      };
    }
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    localStorage.removeItem(this.skyoneTokenKey);
    localStorage.removeItem(this.loginTimeKey);
  }

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem(this.userKey);
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    
    const loginTime = localStorage.getItem(this.loginTimeKey);
    if (!loginTime) return false;
    
    const twelveHours = 2 * 60 * 60 * 1000;
    const isExpired = Date.now() - parseInt(loginTime) > twelveHours;
    
    if (isExpired) {
      this.logout();
      return false;
    }
    
    return true;
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  getSkyOneToken(): string | null {
    return localStorage.getItem(this.skyoneTokenKey);
  }

  // Helper method to get axios instance with auth header
  getAuthenticatedAxios() {
    const token = this.getToken();
    return axios.create({
      baseURL: this.baseURL,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  }
}

const authService = new AuthService();
export default authService;
